import { faTimeline } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Book, LoaderCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';

import { ReferencesDict } from '../../../../../../../../../../../../backend/src/types/types';
import { useCreateChronologyFromThread } from '@/api/mutations/useCreateChronologyFromThread';
import { Button } from '@/components/atoms/new-button';
import { NewChip } from '@/components/atoms/NewChip';
import FactDropdown from '@/components/organisms/FactDropdown';
import { useStore } from '@/store';
import { ConfidenceLevelObject, Fact } from '@/types';

export interface SupportingEvidenceProps {
  confidenceLevel: ConfidenceLevelObject;
  facts: Fact[];
  references: ReferencesDict;
}

export function SupportingFacts({ confidenceLevel, facts, references }: SupportingEvidenceProps) {
  const hasNoContent = facts.length === 0;
  const { matterId, subsetId, threadId } = useParams();

  if (hasNoContent || !matterId || !threadId || !subsetId) {
    return null;
  }

  if (facts.length === 0) {
    return null;
  }

  const { openReferenceDrawer } = useStore();

  const referencesByFactId = references
    ? Object.fromEntries(Object.entries(references).map(([key, value]) => [value.id, key]))
    : null;

  const navigate = useNavigate();
  const { mutate: createChronologyFromThread, isLoading: isCreatingChronology } = useCreateChronologyFromThread();

  return (
    <div className="mt-4">
      <div className="mx-2 mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Book className="text-brandSecondary mb-0.5 size-4.5" />

          <h2 className="text-sm font-semibold text-gray-800">
            {confidenceLevel.text === 'Low' ? 'Possible Information' : 'Supporting Evidence'}
          </h2>
        </div>

        <Button
          className="relative h-auto border-gray-300 bg-gray-50 px-2.5 py-1.5 text-xs hover:border-gray-400"
          variant={'outline'}
          onClick={() =>
            createChronologyFromThread(
              { caseId: matterId, threadId },
              {
                onSuccess: (data) => {
                  navigate(`/matters/${matterId}/subsets/${subsetId}/chronology/${data.chronologyId}`);
                },
              },
            )
          }
          disabled={isCreatingChronology}
        >
          {isCreatingChronology ? (
            <LoaderCircle className="animate-spin text-gray-600" />
          ) : (
            <FontAwesomeIcon icon={faTimeline} size="sm" className="text-gray-600" />
          )}
          Create Chronology from Evidence
          <NewChip />
        </Button>
      </div>

      <div className="mb-2 flex flex-col gap-2">
        {facts.map((fact) => {
          return (
            <div
              className="animate-fade-in rounded-lg border transition-all duration-300 ease-out"
              key={fact.id}
              id={fact.id}
            >
              <FactDropdown
                fact={fact}
                reference={parseInt(referencesByFactId?.[fact.id] ?? '0')}
                hideRelevanceIndicator
                compressedVersion
                onViewDocument={() => openReferenceDrawer({ factId: fact.id, caseId: fact.caseId })}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
